/*
 * Copyright (c) 2025 Cozy Intellect Inc.
 * File Path: packages/sharedlib/src/stores/ui-calendar-events/utils.ts
 */

import type { CalendarEvent, Memory, MemoryDay, Transcript } from '@repo/common';
import type {
  UICalendarEvent,
  UICalendarEventsByDate,
  UICalendarEventsBySection,
  UICalendarTransformContext
} from '@repo/common/types/UICalendar';
import { safeToISOString } from '@repo/toolslib/src/utils/date.util';
import { isTranscriptFull } from '@repo/toolslib/src/utils/transcript.util';
import { addDays, format, isToday, startOfDay } from 'date-fns';
import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';

/**
 * Transform MemoryDay events to UICalendarEvent objects with pre-computed UI properties.
 * This is the core transformation that eliminates component-level mapping logic.
 */
export function transformMemoryDaysToUIEvents(
  memoryDays: MemoryDay[],
  context: UICalendarTransformContext
): UICalendarEvent[] {
  const uiEvents: UICalendarEvent[] = [];

  for (const memoryDay of memoryDays) {
    const events = memoryDay.events || [];

    for (const memory of events) {
      try {
        const uiEvent = transformMemoryToUIEvent(memory, context);
        if (uiEvent) {
          uiEvents.push(uiEvent);
        }
      } catch (error) {
        console.warn('Failed to transform memory to UI event:', memory.id, error);
      }
    }
  }

  return uiEvents;
}

/**
 * Transform a single Memory object to UICalendarEvent.
 * Handles both calendar events and transcript events uniformly.
 */
export function transformMemoryToUIEvent(
  memory: Memory,
  context: UICalendarTransformContext
): UICalendarEvent | null {
  const isCalendarEvent = memory.source === 'calendar' && memory.calendarEvent;
  const isTranscriptEvent = memory.source === 'recording' && memory.transcriptId;

  if (!isCalendarEvent && !isTranscriptEvent) {
    return null; // Skip unknown event types
  }

  if (isCalendarEvent) {
    return createUICalendarEventFromCalendar(memory, context);
  } else {
    return createUICalendarEventFromTranscript(memory, context);
  }
}

/**
 * Create UICalendarEvent from calendar event memory.
 */
export function createUICalendarEventFromCalendar(
  memory: Memory,
  context: UICalendarTransformContext
): UICalendarEvent {
  const event = memory.calendarEvent as CalendarEvent;

  // Detect if this is an all-day event based on date format
  const isAllDay = isAllDayEvent(safeToISOString(event.start), safeToISOString(event.end));

  // Parse dates properly for all-day vs timed events
  const startDate = isAllDay
    ? parseAllDayDate(safeToISOString(event.start))
    : new Date(event.start || memory.datetime || Date.now());
  const endDate = isAllDay
    ? parseAllDayDate(safeToISOString(event.end))
    : new Date(event.end || memory.datetime || Date.now());

  // Validate the created dates
  if (isNaN(startDate.getTime())) {
    console.warn(
      `Invalid startDate for calendar event ${memory.id}:`,
      event.start,
      memory.datetime
    );
    throw new Error(`Invalid startDate for calendar event ${memory.id}`);
  }
  if (isNaN(endDate.getTime())) {
    console.warn(
      `Invalid endDate for calendar event ${memory.id}:`,
      endDate,
      event.end,
      memory.datetime
    );
    throw new Error(`Invalid endDate for calendar event ${memory.id}`);
  }

  // Get associated transcript if available
  const transcript = context.transcriptsByEventId.get(event.uid || '');
  const { hasSummary, isProcessing } = computeTranscriptStatus(transcript);

  return {
    // Core identification
    id: event.uid || memory.id,
    uid: event.uid || memory.id,
    title: event.title || memory.name || '',
    description: event.description || '',
    isFavorite: (event as any).isFavorite,

    // Temporal data
    start: startDate,
    end: endDate,
    dateStr: format(startDate, 'yyyy-MM-dd'),
    dayOfWeek: format(startDate, 'EEEE'),

    // Location and metadata
    location: event.location || null,
    calendarId: event.calendarId || '',
    providerId: event.providerId || '',
    workspaceId: (event as any).workspaceId || memory.workspaceId, // Use enriched event workspaceId first
    tags: (event as any).storedTags || [], // Tags from enriched stored calendar event data

    // Polymorphic source
    source: 'calendar' as const,
    transcriptId: transcript?.id,

    // Pre-computed UI state
    hasSummary,
    isProcessing,
    isToday: isToday(startDate),
    isUpcoming: isAfter(startDate, context.currentDate),
    isPast: isBefore(startDate, context.currentDate),
    timeRemaining: isToday(startDate)
      ? getTimeRemaining(startDate, context.currentDate)
      : undefined,

    // Pre-computed popover display properties
    displayTitle: event.title || memory.name || '',
    compactTimeRange: getCompactTimeRange(startDate, endDate, isAllDay),
    isAllDay,
    categoryName: event.calendarId || 'Calendar',
    categoryIcon: 'calendar', // Calendar icon
    workspaceColor: getWorkspaceColorForEvent(memory.workspaceId, context),
    workspaceName: getWorkspaceNameForEvent(memory.workspaceId, context),

    // Original references
    originalEvent: event,
    originalTranscript: transcript
  };
}

/**
 * Create UICalendarEvent from transcript event memory.
 */
export function createUICalendarEventFromTranscript(
  memory: Memory,
  context: UICalendarTransformContext
): UICalendarEvent {
  const transcript = context.transcriptsMap.get(memory.transcriptId!);
  if (!transcript) {
    throw new Error(`Transcript not found: ${memory.transcriptId}`);
  }

  // Validate and create dates with fallbacks - prefer transcript.date, then memory.datetime
  const transcriptDate = transcript.date || memory.datetime;
  const startDate = transcriptDate ? new Date(transcriptDate) : new Date();
  const endDate = new Date(startDate);
  endDate.setMinutes(endDate.getMinutes() + 30);

  // Validate the created dates
  if (isNaN(startDate.getTime())) {
    console.warn(`Invalid startDate for memory ${memory.id}:`, transcriptDate);
    throw new Error(`Invalid startDate for memory ${memory.id}`);
  }
  if (isNaN(endDate.getTime())) {
    console.warn(`Invalid endDate for memory ${memory.id}:`, endDate, memory.endTime);
    throw new Error(`Invalid endDate for memory ${memory.id}`);
  }
  const { hasSummary, isProcessing } = computeTranscriptStatus(transcript);

  // Create synthetic calendar event for compatibility
  const syntheticEvent: CalendarEvent = {
    uid: memory.id,
    title: String(memory.name || transcript.name || ''),
    description: '',
    start: startDate,
    end: endDate,
    location: null,
    notes: null,
    url: null,
    attendees: null,
    conferenceData: null,
    createdAt: startDate,
    updatedAt: startDate,
    deletedAt: undefined,
    providerId: 'synthetic-transcript',
    calendarId: 'recordings',
    workspaceId: memory.workspaceId
  };

  return {
    // Core identification
    id: memory.id,
    uid: memory.id,
    title: String(memory.name || transcript.name || ''),
    description: '',
    isFavorite: transcript.isFavorite,

    // Temporal data
    start: startDate,
    end: endDate,
    dateStr: format(startDate, 'yyyy-MM-dd'),
    dayOfWeek: format(startDate, 'EEEE'),

    // Location and metadata
    location: null,
    calendarId: 'recordings',
    providerId: 'synthetic-transcript',
    workspaceId: memory.workspaceId,

    // Polymorphic source
    source: 'recording' as const,
    transcriptId: transcript.id,

    // Pre-computed UI state
    hasSummary,
    isProcessing,
    isToday: isToday(startDate),
    isUpcoming: isAfter(startDate, context.currentDate),
    isPast: isBefore(startDate, context.currentDate),
    timeRemaining: isToday(startDate)
      ? getTimeRemaining(startDate, context.currentDate)
      : undefined,

    // Pre-computed popover display properties
    displayTitle: String(memory.name || transcript.name || ''),
    compactTimeRange: getCompactTimeRange(startDate, endDate, false), // Transcripts are never all-day
    isAllDay: false, // Transcript events are always timed
    categoryName: 'Recording',
    categoryIcon: 'microphone', // Microphone icon
    workspaceColor: getWorkspaceColorForEvent(memory.workspaceId, context),
    workspaceName: getWorkspaceNameForEvent(memory.workspaceId, context),

    // Original references
    originalEvent: syntheticEvent,
    originalTranscript: transcript
  };
}

/**
 * Compute transcript status for UI display.
 * Handles both TranscriptSummary (with denormalized summary field) and TranscriptFull objects.
 */
export function computeTranscriptStatus(transcript?: Transcript): {
  hasSummary: boolean;
  isProcessing: boolean;
} {
  if (!transcript) {
    return { hasSummary: false, isProcessing: false };
  }

  // For TranscriptSummary: check denormalized summary field
  // For TranscriptFull: check meeting.minutes.summary
  let summaryLength = 0;

  if (isTranscriptFull(transcript)) {
    summaryLength = transcript.meeting?.minutes?.summary?.length || 0;
  } else {
    // TranscriptSummary - use denormalized summary field
    summaryLength = transcript.summary?.length || 0;
  }

  const hasSummary = summaryLength > 0;
  const isProcessing = !!transcript && !hasSummary && transcript.status === 'transcribing';

  return { hasSummary, isProcessing };
}

/**
 * Calculate time remaining for today's events.
 */
export function getTimeRemaining(eventStart: Date, currentDate: Date): string | undefined {
  if (!isToday(eventStart)) {
    return undefined;
  }

  const diffInMinutes = Math.floor((eventStart.getTime() - currentDate.getTime()) / (1000 * 60));

  if (diffInMinutes <= 0) {
    return undefined; // Event has started or passed
  }

  if (diffInMinutes < 60) {
    return `In ${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''}`;
  }

  const hours = Math.floor(diffInMinutes / 60);
  return `In ${hours} hour${hours !== 1 ? 's' : ''}`;
}

/**
 * Group UI events by date string for efficient lookups.
 */
export function groupEventsByDate(events: UICalendarEvent[]): UICalendarEventsByDate {
  const grouped: UICalendarEventsByDate = {};

  for (const event of events) {
    if (!grouped[event.dateStr]) {
      grouped[event.dateStr] = [];
    }
    grouped[event.dateStr].push(event);
  }

  return grouped;
}

/**
 * Group UI events by time sections for agenda views.
 */
export function groupEventsBySection(
  events: UICalendarEvent[],
  currentDate: Date
): UICalendarEventsBySection {
  const today = startOfDay(currentDate);
  const tomorrow = addDays(today, 1);

  const sections: UICalendarEventsBySection = {
    today: [],
    tomorrow: [],
    upcoming: [],
    recent: []
  };

  for (const event of events) {
    const eventDate = startOfDay(event.start);

    if (event.isToday) {
      sections.today.push(event);
    } else if (eventDate.getTime() === tomorrow.getTime()) {
      sections.tomorrow.push(event);
    } else if (event.isUpcoming) {
      sections.upcoming.push(event);
    } else if (event.isPast) {
      sections.recent.push(event);
    }
  }

  return sections;
}

/**
 * Remove duplicate memory events across different view stores.
 */
export function deduplicateMemoryDays(memoryDays: MemoryDay[]): MemoryDay[] {
  const seenEventIds = new Set<string>();
  const uniqueDays: MemoryDay[] = [];

  for (const memoryDay of memoryDays) {
    const uniqueEvents = memoryDay.events.filter((event) => {
      const eventId = event.calendarEvent?.uid || event.transcriptId || event.id;
      if (seenEventIds.has(eventId)) {
        return false;
      }
      seenEventIds.add(eventId);
      return true;
    });

    if (uniqueEvents.length > 0) {
      uniqueDays.push({
        ...memoryDay,
        events: uniqueEvents
      });
    }
  }

  return uniqueDays;
}

/**
 * Validate UICalendarEvent object for completeness.
 */
export function validateUICalendarEvent(event: UICalendarEvent): boolean {
  return !!(
    event.id &&
    event.title &&
    event.start &&
    event.end &&
    event.dateStr &&
    event.source &&
    event.originalEvent
  );
}

/**
 * Sort UICalendarEvents by start time.
 */
export function sortEventsByTime(events: UICalendarEvent[]): UICalendarEvent[] {
  return [...events].sort((a, b) => a.start.getTime() - b.start.getTime());
}

/**
 * Filter events by enabled calendars.
 */
export function filterEventsByEnabledCalendars(
  events: UICalendarEvent[],
  enabledCalendars: string[]
): UICalendarEvent[] {
  if (enabledCalendars.length === 0) {
    return events; // No filter if no calendars specified
  }

  return events.filter(
    (event) => enabledCalendars.includes(event.calendarId) || event.source === 'recording' // Always include transcript events
  );
}

/**
 * Filter events by date range.
 */
export function filterEventsByDateRange(
  events: UICalendarEvent[],
  dateRange: { start: string; end: string }
): UICalendarEvent[] {
  const startDate = new Date(dateRange.start);
  const endDate = new Date(dateRange.end);

  return events.filter((event) => event.start >= startDate && event.start <= endDate);
}

/**
 * Get event statistics for debugging.
 */
export function getEventStatistics(events: UICalendarEvent[]): {
  total: number;
  bySource: { calendar: number; recording: number };
  byStatus: { withSummary: number; processing: number; completed: number };
  dateRange: { earliest: Date | null; latest: Date | null };
} {
  const stats = {
    total: events.length,
    bySource: { calendar: 0, recording: 0 },
    byStatus: { withSummary: 0, processing: 0, completed: 0 },
    dateRange: { earliest: null as Date | null, latest: null as Date | null }
  };

  for (const event of events) {
    // Source statistics
    stats.bySource[event.source]++;

    // Status statistics
    if (event.hasSummary) {
      stats.byStatus.withSummary++;
    } else if (event.isProcessing) {
      stats.byStatus.processing++;
    } else {
      stats.byStatus.completed++;
    }

    // Date range
    if (!stats.dateRange.earliest || event.start < stats.dateRange.earliest) {
      stats.dateRange.earliest = event.start;
    }
    if (!stats.dateRange.latest || event.start > stats.dateRange.latest) {
      stats.dateRange.latest = event.start;
    }
  }

  return stats;
}

/**
 * Get compact time range string for popover display.
 */
function getCompactTimeRange(start: Date, end: Date, isAllDay: boolean = false): string {
  try {
    // Validate dates before formatting
    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
      return 'Invalid time';
    }

    // Handle all-day events
    if (isAllDay) {
      return 'All day';
    }

    const startTime = format(start, 'h:mm a');
    const endTime = format(end, 'h:mm a');
    return `${startTime} - ${endTime}`;
  } catch (error) {
    console.error('Error formatting time range:', error, { start, end });
    return 'Invalid time';
  }
}

/**
 * Get workspace color for event (placeholder - will be enhanced when workspace store is available).
 */
function getWorkspaceColorForEvent(
  _workspaceId: string | undefined,
  _context: UICalendarTransformContext
): string | undefined {
  // TODO: Integrate with workspace store from context when available
  return undefined; // Placeholder for now
}

/**
 * Get workspace name for event (placeholder - will be enhanced when workspace store is available).
 */
function getWorkspaceNameForEvent(
  _workspaceId: string | undefined,
  _context: UICalendarTransformContext
): string | undefined {
  // TODO: Integrate with workspace store from context when available
  return undefined; // Placeholder for now
}

/**
 * Detect if an event is all-day based on the date format.
 * All-day events typically have date strings without time information.
 */
function isAllDayEvent(start: string | undefined, end: string | undefined): boolean {
  if (!start || !end) return false;

  // Check if the start and end are date-only strings (YYYY-MM-DD format)
  const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;
  return dateOnlyRegex.test(start) && dateOnlyRegex.test(end);
}

/**
 * Parse an all-day date string to a Date object.
 * For all-day events, we want to create dates at the start of the day in local time.
 */
function parseAllDayDate(dateStr: string | undefined): Date {
  if (!dateStr) return new Date();

  // For date-only strings like "2025-07-19", create a date at start of day in local time
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
}
